import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { WordMark } from '../../components/WordMark';

/*
 * What we changed.
 *
 * WHY THIS PAGE EXISTS. This project's pitch has two halves — a voice, and
 * "Battlegrounds with the known pain points addressed" — and only the first is
 * visible. A player who knows Battlegrounds will not notice that a frustration
 * was fixed unless something says so; a player who does not know it cannot
 * notice at all. So the work exists and the credit does not.
 *
 * EVERY LINE BELOW WAS VERIFIED IN SOURCE BEFORE IT WAS WRITTEN, not recalled.
 * That is not pedantry — an earlier pass wrote that this game had no touch
 * inspect, citing its own tooltip audit, and that audit's gap list had been
 * superseded by its own rollout section two headings later. The code was on
 * main the whole time. A claims page is exactly where that mistake becomes
 * public, so each item names the file that makes it true.
 */

interface Change {
  /** The frustration, in the words a player would use. */
  pain: string;
  /** What this game does instead. */
  fix: string;
  /** Where it lives, so the claim is checkable. */
  where: string;
}

const CHANGES: Change[] = [
  {
    pain: 'The shop clock runs while you watch the fight, so a long combat leaves you seconds to buy.',
    fix: 'The clock stops for combat and starts fresh when the shop opens. Watching costs you nothing.',
    where: 'stores/match.ts — turnTimerActive goes false when combat begins',
  },
  {
    pain: 'Combat is as slow as it is on the first turn, forever.',
    fix: 'Speed controls sit next to the fight, not three menus deep. One tap for 2x or 4x.',
    where: 'game/combat/CombatPage.tsx — Speed = 1 | 2 | 4',
  },
  {
    pain: 'You pocket your phone mid-round and come back to a turn that ended without you.',
    fix: 'The clock pauses when the app is backgrounded, and while any modal is open.',
    where: 'game/tavern/TavernPage.tsx — paused on document.hidden',
  },
  {
    pain: 'You cannot read a card properly on a phone, because the detail is a hover.',
    fix: 'Long-press any card for the full panel. Built for touch rather than ported from a mouse.',
    where: 'components/Explain.tsx — 500ms press, 10px slop',
  },
  {
    pain: 'Lose connection mid-match and the match is gone.',
    fix: 'The server runs combat, and if it cannot be reached your own device runs the same engine. The round finishes either way.',
    where: 'net/asyncClient.ts — every call returns null and falls back',
  },
  {
    pain: 'Animation you cannot turn down.',
    fix: 'Reduced motion is respected in the fight itself, not just on the menus.',
    where: 'components/useReducedMotion.ts',
  },
];

export function ChangesPage() {
  return (
    <main className="pt-safe relative min-h-dvh w-full overflow-x-hidden bg-asphalt">
      <section className="relative mx-auto flex h-full w-full max-w-3xl flex-col gap-md px-md py-md md:gap-lg md:px-lg md:py-lg lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <WordMark size="sm" />
          <Link
            to="/menu"
            className="flex min-h-[44px] items-center px-sm font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker"
          >
            back
          </Link>
        </header>

        <div className="flex flex-col gap-xs">
          <h1 className="font-display text-[28px] leading-none text-bone md:text-[36px]">
            What we changed
          </h1>
          <p className="max-w-prose text-[14px] leading-relaxed text-graphite md:text-[15px]">
            This is an auto-battler in the Battlegrounds shape. These are the
            things about that shape we found frustrating, and what this one does
            instead.
          </p>
        </div>

        <ol className="flex flex-col gap-sm">
          {CHANGES.map((c, i) => (
            <motion.li
              key={c.where}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.25 }}
              className="rounded-sm border border-stucco3 bg-stucco2 p-md"
            >
              <p className="font-data text-[11px] uppercase tracking-[0.25em] text-graphite">
                the annoyance
              </p>
              <p className="mt-xs text-[15px] leading-relaxed text-bone">{c.pain}</p>

              <p className="mt-sm font-data text-[11px] uppercase tracking-[0.25em] text-marker">
                what we do
              </p>
              <p className="mt-xs text-[15px] leading-relaxed text-bone">{c.fix}</p>

              {/* The receipt. A claim nobody can check is a boast. */}
              <p className="mt-sm font-data text-[10px] leading-relaxed text-graphite md:text-[11px]">
                {c.where}
              </p>
            </motion.li>
          ))}
        </ol>

        <p className="max-w-prose text-[13px] leading-relaxed text-graphite">
          Every line above names the file that makes it true, because a claims
          page is the easiest place in a project to be wrong out loud.
        </p>
      </section>
    </main>
  );
}
